import { useTranslation } from "react-i18next";
import { IconButton, makeStyles, Typography } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CloseIcon from "@material-ui/icons/Close";
import { Outcome } from "../helpers";
import { useResultStyles } from "./styles";

interface Props {
  outcome: Outcome;
  onClose: () => void;
}

export default function Success({ outcome, onClose }: Props) {
  const { t } = useTranslation("storage");
  const resultClasses = useResultStyles();
  const classes = useStyles();
  const { probe = {} } = outcome;
  const titleKey =
    outcome.mode === "save"
      ? "externalAI.result.saveOk"
      : "externalAI.result.testOk";
  const usage = t("externalAI.result.usageValue", {
    prompt: probe.usage?.prompt_tokens ?? "-",
    completion: probe.usage?.completion_tokens ?? "-",
  });
  const latency = t("externalAI.result.latencyValue", {
    millis: probe.latencyMillis ?? "-",
  });
  const status = probe.status === 200 ? "200 OK" : String(probe.status ?? "-");
  const stats = [
    {
      label: t("externalAI.result.status"),
      value: status,
      className: classes.statValue,
    },
    {
      label: t("externalAI.result.servedModel"),
      value: probe.servedModel || "-",
      className: `${classes.statValue} ${classes.mono}`,
    },
    {
      label: t("externalAI.result.usage"),
      value: usage,
      className: classes.statValue,
    },
    {
      label: t("externalAI.result.latency"),
      value: latency,
      className: classes.statValue,
    },
  ];

  return (
    <div
      role="alert"
      className={`${resultClasses.result} ${classes.panel} ${classes.success}`}
      data-testid="external-ai__result-ok"
    >
      <div className={classes.panelHeader}>
        <CheckCircleIcon className={classes.successIcon} />
        <div className={classes.panelText}>
          <Typography variant="body1" className={classes.panelTitle}>
            {t(titleKey)}
          </Typography>
          <Typography variant="body2">
            {t("externalAI.result.okDescription")}
          </Typography>
        </div>
        <IconButton
          size="small"
          aria-label="Close"
          className={classes.successClose}
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      <div className={classes.stats}>
        {stats.map(stat => (
          <div className={classes.stat} key={stat.label}>
            <Typography
              variant="caption"
              color="textSecondary"
              className={classes.statLabel}
            >
              {stat.label}
            </Typography>
            <Typography variant="body2" className={stat.className}>
              {stat.value}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
}

const useStyles = makeStyles(theme => ({
  success: { borderColor: "#bfe5cc", backgroundColor: "#f1faf4" },
  panel: { padding: "14px 16px", borderRadius: 4 },
  panelHeader: { display: "flex", alignItems: "flex-start", gap: 12 },
  panelText: { flex: 1, minWidth: 0 },
  panelTitle: { fontWeight: 500, lineHeight: 1.4 },
  successIcon: { color: theme.palette.success.main },
  successClose: {
    marginTop: -3,
    marginRight: -5,
    color: theme.palette.success.main,
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 10,
    marginTop: 14,
  },
  stat: {
    display: "flex",
    flexDirection: "column",
    padding: "10px 12px",
    border: "1px solid #dcefe2",
    borderRadius: 6,
    backgroundColor: "#fff",
  },
  statLabel: { fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase" },
  statValue: { fontWeight: 500, wordBreak: "break-word" },
  mono: { fontFamily: "monospace", fontWeight: 400 },
}));
